import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Shield, Lock, Eye, FileText, UserX, Download } from 'lucide-react';

const GDPRCompliance = () => {
  const features = [
    {
      icon: Shield,
      title: 'Data Protection',
      description: 'We implement robust security measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction.'
    },
    {
      icon: Lock,
      title: 'Secure Processing',
      description: 'All data is processed securely using industry-standard encryption protocols both in transit and at rest.'
    },
    {
      icon: Eye,
      title: 'Transparency',
      description: 'We provide clear information about how we collect, use, and store your data, ensuring complete transparency.'
    },
    {
      icon: FileText,
      title: 'Data Minimization',
      description: 'We only collect and process data that is necessary for providing our services, adhering to data minimization principles.'
    },
    {
      icon: UserX,
      title: 'Right to Erasure',
      description: 'You have the right to request deletion of your personal data, and we will comply within the legal timeframe.'
    },
    {
      icon: Download,
      title: 'Data Portability',
      description: 'You can request a copy of your data in a structured, commonly used, and machine-readable format.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">GDPR Compliance</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We are committed to protecting your privacy and ensuring compliance with the General Data Protection Regulation (GDPR)
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <div key={idx} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            );
          })}
        </div>

        {/* Detailed Information */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Our GDPR Commitment</h2>
            <p className="text-gray-700 leading-relaxed">
              As a service provider handling personal data, we take our responsibilities under the GDPR seriously. We have implemented comprehensive policies, procedures, and technical measures to ensure compliance with all GDPR requirements and to protect the rights of individuals whose data we process.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Legal Basis for Processing</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We process personal data only when we have a valid legal basis:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Consent:</strong> You have given clear consent for us to process your personal data for a specific purpose</li>
              <li><strong>Contract:</strong> Processing is necessary for a contract we have with you</li>
              <li><strong>Legal Obligation:</strong> Processing is necessary for us to comply with the law</li>
              <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate interests or those of a third party</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your Rights Under GDPR</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under GDPR, you have the following rights regarding your personal data:
            </p>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Right to Access</h3>
                <p className="text-gray-700 text-sm">
                  You have the right to request copies of your personal data. We may charge a small fee for this service.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Right to Rectification</h3>
                <p className="text-gray-700 text-sm">
                  You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Right to Erasure</h3>
                <p className="text-gray-700 text-sm">
                  You have the right to request that we erase your personal data, under certain conditions.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Right to Restrict Processing</h3>
                <p className="text-gray-700 text-sm">
                  You have the right to request that we restrict the processing of your personal data, under certain conditions.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Right to Object</h3>
                <p className="text-gray-700 text-sm">
                  You have the right to object to our processing of your personal data, under certain conditions.
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Right to Data Portability</h3>
                <p className="text-gray-700 text-sm">
                  You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Protection Officer</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We have appointed a Data Protection Officer (DPO) who is responsible for overseeing our data protection strategy and implementation to ensure compliance with GDPR requirements.
            </p>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-gray-700"><strong>DPO Contact:</strong></p>
              <p className="text-gray-700 mt-2">Email: dpo@whatsappbusiness.com</p>
              <p className="text-gray-700">Phone: +91 9866461437</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Breach Notification</h2>
            <p className="text-gray-700 leading-relaxed">
              In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify you and the relevant supervisory authority within 72 hours of becoming aware of the breach, as required by GDPR Article 33 and 34.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              When we transfer personal data outside the European Economic Area (EEA), we ensure that appropriate safeguards are in place, such as:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>Standard Contractual Clauses approved by the European Commission</li>
              <li>Adequacy decisions confirming adequate protection in the destination country</li>
              <li>Binding Corporate Rules for intra-group transfers</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain personal data only for as long as necessary to fulfill the purposes for which it was collected, including legal, accounting, or reporting requirements. Our retention periods are based on:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>The amount, nature, and sensitivity of the personal data</li>
              <li>The purposes for which we process the data</li>
              <li>Legal and regulatory requirements</li>
              <li>Potential risk of harm from unauthorized use or disclosure</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Third-Party Processors</h2>
            <p className="text-gray-700 leading-relaxed">
              We work with third-party service providers who process personal data on our behalf. We ensure that all such processors:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>Implement appropriate technical and organizational security measures</li>
              <li>Process data only according to our documented instructions</li>
              <li>Have signed Data Processing Agreements (DPAs) with us</li>
              <li>Comply with GDPR requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Exercise Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise any of your GDPR rights, please contact us using the details below. We will respond to your request within one month, though we may extend this by two additional months if your request is complex.
            </p>
            <div className="p-6 bg-gray-50 rounded-lg">
              <p className="text-gray-700 font-semibold mb-2">Contact Information:</p>
              <p className="text-gray-700">Email: privacy@whatsappbusiness.com</p>
              <p className="text-gray-700">Phone: +91 9866461437</p>
              <p className="text-gray-700">Address: Mumbai, Maharashtra, India</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Complaints</h2>
            <p className="text-gray-700 leading-relaxed">
              If you believe that your data protection rights have been breached, you have the right to lodge a complaint with your local supervisory authority. However, we encourage you to contact us first so we can address your concerns directly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Updates to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this GDPR Compliance statement from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the new statement on this page and updating the "Last Updated" date.
            </p>
            <p className="text-gray-600 text-sm mt-4">Last updated: January 2025</p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default GDPRCompliance;